// theme.ts — Bootswatch theme persistence via the `theme` cookie, and the
// light/dark scheme it implies for anything that has to match the page.
// Defaults to `darkly` to match the index.html stylesheet.
//
// The workspace editor (Code OSS) is one of those: it is launched in the
// scheme the page is in, and relaunched when the toggle flips. Listeners
// subscribe here rather than reading the cookie, which has no change
// event.
export const getTheme = (): string => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; theme=`);
  if (parts.length === 2) {
    const theme = parts.pop()?.split(';').shift();
    if (theme) return theme;
  }
  return 'darkly'; // Default theme
};

const listeners = new Set<() => void>();

export const setTheme = (theme: string) => {
  const date = new Date();
  date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `theme=${theme};${expires};path=/`;
  listeners.forEach((l) => l());
};

/** The page's scheme: Flatly is the light theme, everything else dark. */
export type ColorScheme = 'light' | 'dark';

export const schemeOf = (theme: string): ColorScheme => (theme === 'flatly' ? 'light' : 'dark');

export const getColorScheme = (): ColorScheme => schemeOf(getTheme());

/** Subscribe to theme changes (useSyncExternalStore-shaped). */
export const subscribeTheme = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
